import React from "react";
import { Plane, Ship, Truck, Route } from "lucide-react";

const MODE_META = {
  air: { Icon: Plane, label: "Air", tint: "#0E2F76" },
  sea: { Icon: Ship, label: "Sea", tint: "#0E2F76" },
  road: { Icon: Truck, label: "Road", tint: "#0E2F76" },
  multimodal: { Icon: Route, label: "Multimodal", tint: "#0E2F76" },
};

export function ModeIcon({ mode, size = 16, showLabel = false, className = "" }) {
  const m = MODE_META[String(mode || "").toLowerCase()] || MODE_META.road;
  const { Icon, label, tint } = m;
  return (
    <span className={`inline-flex items-center gap-1.5 ${className}`} data-testid={`mode-${mode}`}>
      <Icon size={size} color={tint} strokeWidth={2.2} />
      {showLabel && <span className="text-xs font-semibold uppercase tracking-wider">{label}</span>}
    </span>
  );
}

const STATUS_STYLE = {
  AWAITING_APPROVAL:   { bg: "#FEF3C7", fg: "#B45309", label: "Awaiting approval" },
  BOOKED:              { bg: "#EEF3FB", fg: "#0E2F76", label: "Booked" },
  PICKED_UP:           { bg: "#EEF3FB", fg: "#0E2F76", label: "Picked up" },
  IN_TRANSIT:          { bg: "#DBEAFE", fg: "#1D4ED8", label: "In transit" },
  AT_HUB:              { bg: "#DBEAFE", fg: "#1D4ED8", label: "At hub" },
  CUSTOMS:             { bg: "#FEF3C7", fg: "#B45309", label: "Customs" },
  OUT_FOR_DELIVERY:    { bg: "#DBEAFE", fg: "#1D4ED8", label: "Out for delivery" },
  DELIVERED:           { bg: "#DCFCE7", fg: "#166534", label: "Delivered" },
  EXCEPTION:           { bg: "#FEE2E2", fg: "#B91C1C", label: "Exception" },
  // Per-leg statuses
  PENDING:             { bg: "#EEF3FB", fg: "#475569", label: "Planned" },
  COMPLETED:           { bg: "#DCFCE7", fg: "#166534", label: "Completed" },
  paid:                { bg: "#DCFCE7", fg: "#166534", label: "Paid" },
  due:                 { bg: "#FEF3C7", fg: "#B45309", label: "Due" },
  overdue:             { bg: "#FEE2E2", fg: "#B91C1C", label: "Overdue" },
};

export function StatusChip({ status, className = "" }) {
  const s = STATUS_STYLE[status] || { bg: "#E5E7EB", fg: "#111827", label: String(status || "—") };
  return (
    <span
      className={`vtx-chip ${className}`}
      style={{ backgroundColor: s.bg, color: s.fg }}
      data-testid={`status-chip-${status}`}
    >
      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: s.fg }} />
      {s.label}
    </span>
  );
}

export function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { year: "numeric", month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

export function formatDateShort(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "2-digit" });
  } catch { return iso; }
}

export function formatMoney(n, ccy = "USD") {
  if (n == null) return "—";
  try { return new Intl.NumberFormat(undefined, { style: "currency", currency: ccy }).format(n); }
  catch { return `${ccy} ${Number(n).toFixed(2)}`; }
}

/** Centered empty state: icon badge + title + hint + optional CTA. */
export function EmptyState({ icon: Icon, title, hint, children }) {
  return (
    <div className="p-12 md:p-16 text-center" data-testid="empty-state">
      {Icon && (
        <div className="w-12 h-12 mx-auto grid place-items-center rounded-full bg-[#F4FEFF] border border-[#A9C0E0]/60 text-[#0E2F76]">
          <Icon size={22} strokeWidth={2} />
        </div>
      )}
      <div className="mt-4 font-bold text-[#0E2F76]">{title}</div>
      {hint && <div className="text-sm text-[#475569] mt-1 max-w-sm mx-auto">{hint}</div>}
      {children && <div className="mt-4 flex justify-center">{children}</div>}
    </div>
  );
}

/** KPI stat tile used across the dashboards, console and reports. */
export function Kpi({ label, value, Icon, accent = "#0E2F76" }) {
  return (
    <div className="vtx-card p-5 flex items-start justify-between" data-testid={`kpi-${String(label).toLowerCase().replace(/\s+/g, "-")}`}>
      <div>
        <div className="vtx-eyebrow">{label}</div>
        <div className="mt-2 text-3xl font-black text-[#0E2F76]">{value}</div>
      </div>
      <div className="w-9 h-9 grid place-items-center border" style={{ borderColor: accent, color: accent }}>
        <Icon size={18} strokeWidth={2.2} />
      </div>
    </div>
  );
}
